import { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { useMutation, useQueryClient } from "react-query";
import { GraphQLClient } from "graphql-request";
import { Body3 } from "components/Typography";
import { Spinner } from "components";
import { Button } from "library";
import { useGetPeriodoEiduActivo } from "hooks/useRequest";
import { InscripcionEiduForm } from "./InscripcionForm";
import { inscribirEidu } from "./mutation";
import AlertContext from "context/AlertContext";
import {
  mapEiduFormValues,
  mapGruposToTalleresOptions,
} from "utilites/eiduForm";
import { EiduHero } from "./EiduHero";

const API_URL = process.env.REACT_APP_API_URL as string;
const graphQLClient = new GraphQLClient(API_URL);
const EIDU_HOME_URL = "https://eidu.ucr.ac.cr/";
const EIDU_THANK_YOU_MSG =
  "Gracias por su inscripción. Próximamente recibirá un correo con las indicaciones " +
  "para su participación en EIDU. Si tiene alguna consulta puede escribirnos a eidu@urosario.edu.co";

const redirectToEiduHome = () => {
  window.location.assign(EIDU_HOME_URL);
};

export default function EiduInscripcion() {
  const queryClient = useQueryClient();
  const alert = useContext(AlertContext);
  const {
    data: periodo,
    isLoading,
    isError,
    refetch: refetchPeriodoEidu,
  } = useGetPeriodoEiduActivo();
  const [showThankYou, setShowThankYou] = useState(false);
  const [thankYouMessage, setThankYouMessage] = useState("");

  const mutation = useMutation(
    async (variables: Record<string, unknown>) => {
      const data = await graphQLClient.request(inscribirEidu, variables);
      return data.crearDocente;
    },
    {
      onSuccess: (result) => {
        setThankYouMessage(result?.success ?? EIDU_THANK_YOU_MSG);
        setShowThankYou(true);
      },
      onError: (error: any) => {
        const message =
          error?.response?.errors?.[0]?.message ??
          "No se pudo completar la inscripción. Intente nuevamente.";
        /*@ts-ignore*/
        alert.error(message);
        queryClient.invalidateQueries("periodoEiduActivo");
      },
    }
  );

  useEffect(() => {
    if (!showThankYou) return;
    const timer = setTimeout(redirectToEiduHome, 5000);
    return () => clearTimeout(timer);
  }, [showThankYou]);

  const talleres = mapGruposToTalleresOptions(periodo?.grupoSet ?? []);

  const onFinish = (values: any) => {
    if (!periodo?.id) return;
    mutation.mutate(mapEiduFormValues(values, Number(periodo.id)));
  };

  if (isLoading) {
    return (
      <Page>
        <Spinner isLoading title="Cargando periodo EIDU" />
      </Page>
    );
  }

  if (isError || !periodo) {
    return (
      <Page>
        <Body3>
          La inscripción al EIDU no está habilitada en este momento. No hay un
          periodo de matrícula EIDU disponible.
        </Body3>
      </Page>
    );
  }

  if (showThankYou) {
    return (
      <Page>
        <EiduHero />
        <ThankYouBox>
          <Body3>
            <strong>{thankYouMessage}</strong>
          </Body3>
          <Body3 style={{ marginTop: 16, color: "#65696A" }}>
            Será redirigido a la página del EIDU en unos segundos.
          </Body3>
          <StyledButton variant="secondary" onClick={redirectToEiduHome}>
            Ir a la página del EIDU
          </StyledButton>
        </ThankYouBox>
      </Page>
    );
  }

  return (
    <Page>
      <EiduHero />
      <InscripcionEiduForm
        onFinish={onFinish}
        talleres={talleres}
        isSubmitting={mutation.isLoading}
        onRefreshTalleres={() => {
          refetchPeriodoEidu();
        }}
      />
      {mutation.isLoading && (
        <Spinner
          isLoading={mutation.isLoading}
          title="Registrando inscripción"
        />
      )}
    </Page>
  );
}

const Page = styled.div`
  padding: 26px 16px;
  @media (min-width: 768px) {
    padding: 26px 86px;
  }
  @media (min-width: 1200px) {
    padding: 0px 100px;
    max-width: 1200px;
    margin: 0 auto;
  }
`;

const ThankYouBox = styled.div`
  max-width: 640px;
  margin-top: 32px;
`;

const StyledButton = styled(Button)`
  margin-top: 32px;
`;
